import { useState, useEffect, FunctionComponent} from "react";
import { Button, IconButton, Chip, Divider, TextField, Snackbar, Dialog } from "../../../tailwindcomponents";
import { Close } from '../../../components/icons';
import { Team } from "../../../types/maintenanceTeams";
import { User } from "../../../types/user";
import EmployeeSearch from "../../../components/EmployeeSearch/EmployeeSearch";
import MaintenanceTeamsApi from "../../../api/maintenanceTeams";
import { ProfileImg } from "../../../components";

type AlertColor = 'success' | 'info' | 'warning' | 'error';

interface Props {
  team: Team | null;
  employees: User[];
  onTeamUpdated?: () => void;
}

const EditTeam: FunctionComponent<Props> = ({ team, employees, onTeamUpdated }) => {
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [resetEmployeeSearch, setResetEmployeeSearch] = useState(false);
  const [resetTeamLeaderSearch, setResetTeamLeaderSearch] = useState(false);
  const [teamName, setTeamName] = useState(team?.TeamName || "");
  const [isUpdatingTeam, setIsUpdatingTeam] = useState(false);
  const [isDeletingTeam, setIsDeletingTeam] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [removeMemberDialogOpen, setRemoveMemberDialogOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<{id: number, name: string} | null>(null);
  const [isRemovingMember, setIsRemovingMember] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>("success");

  // Update team name when team changes
  useEffect(() => {
    if (team) {
      setTeamName(team.TeamName);
    }
  }, [team]);

  const handleChangeTeamLeader = (employeeId: number) => {
    if (!team || employeeId === 0) return;
    
    // Check if employee is the current team leader
    if (employeeId === team.TeamLeaderId) {
      setSnackbarMessage("This employee is already the team leader");
      setSnackbarSeverity("warning");
      setResetTeamLeaderSearch(true);
      setTimeout(() => setResetTeamLeaderSearch(false), 100);
      return;
    }

    setIsUpdatingTeam(true);
    MaintenanceTeamsApi.EditTeam(team.Id, team.TeamName, employeeId)
      .then(response => {
        if (response.status) {
          setSnackbarMessage("Team leader updated successfully");
          setSnackbarSeverity("success");
          setResetTeamLeaderSearch(true);
          setTimeout(() => setResetTeamLeaderSearch(false), 100);
          if (onTeamUpdated) {
            onTeamUpdated();
          }
        } else {
          setSnackbarMessage(response.message || "Failed to update team leader");
          setSnackbarSeverity("error");
        }
      })
      .catch(() => {
        setSnackbarMessage("An error occurred while updating the team leader");
        setSnackbarSeverity("error");
      })
      .finally(() => {
        setIsUpdatingTeam(false);
      });
  };

  const handleSaveTeamName = () => {
    if (!team || !teamName.trim()) return;
    
    if (teamName.trim() === team.TeamName) {
      setSnackbarMessage("Team name hasn't changed");
      setSnackbarSeverity("warning");
      return;
    }

    setIsUpdatingTeam(true);
    MaintenanceTeamsApi.EditTeam(team.Id, teamName.trim(), team.TeamLeaderId)
      .then(response => {
        if (response.status) {
          setSnackbarMessage("Team name updated successfully");
          setSnackbarSeverity("success");
          if (onTeamUpdated) {
            onTeamUpdated();
          }
        } else {
          setSnackbarMessage(response.message || "Failed to update team name");
          setSnackbarSeverity("error");
        }
      })
      .catch(() => {
        setSnackbarMessage("An error occurred while updating the team name");
        setSnackbarSeverity("error");
      })
      .finally(() => {
        setIsUpdatingTeam(false);
      });
  };

  const handleDeleteTeam = () => {
    if (!team) return;
    setDeleteDialogOpen(true);
  };

  const confirmDeleteTeam = (confirmed: boolean) => {
    setDeleteDialogOpen(false);
    
    if (!confirmed || !team) return;

    setIsDeletingTeam(true);
    MaintenanceTeamsApi.DeleteTeam(team.Id)
      .then(response => {
        if (response.status) {
          setSnackbarMessage("Team deleted successfully");
          setSnackbarSeverity("success");
          if (onTeamUpdated) {
            onTeamUpdated();
          }
        } else {
          setSnackbarMessage(response.message || "Failed to delete team");
          setSnackbarSeverity("error");
        }
      })
      .catch(() => {
        setSnackbarMessage("An error occurred while deleting the team");
        setSnackbarSeverity("error");
      })
      .finally(() => {
        setIsDeletingTeam(false);
      });
  };

  const handleRemoveMemberClick = (memberId: number, memberName: string) => {
    setMemberToRemove({ id: memberId, name: memberName });
    setRemoveMemberDialogOpen(true);
  };

  const confirmRemoveMember = (confirmed: boolean) => {
    setRemoveMemberDialogOpen(false);
    
    if (!confirmed || !team || !memberToRemove) {
      setMemberToRemove(null);
      return;
    }

    setIsRemovingMember(true);
    MaintenanceTeamsApi.RemoveTeamMember(team.Id, memberToRemove.id)
      .then(response => {
        if (response.status) {
          setSnackbarMessage("Member removed successfully");
          setSnackbarSeverity("success");
          if (onTeamUpdated) {
            onTeamUpdated();
          }
        } else {
          setSnackbarMessage(response.message || "Failed to remove member");
          setSnackbarSeverity("error");
        }
      })
      .catch(() => {
        setSnackbarMessage("An error occurred while removing the member");
        setSnackbarSeverity("error");
      })
      .finally(() => {
        setIsRemovingMember(false);
        setMemberToRemove(null);
      });
  };

  const handleAddMember = (employeeId: number) => {
    if (!team || employeeId === 0) return;
    
    const isAlreadyMember = team.TeamMembers?.some(m => m.TeamMemberId === employeeId);
    if (isAlreadyMember) {
      setSnackbarMessage("This employee is already a member of the team");
      setSnackbarSeverity("warning");
      setResetEmployeeSearch(true);
      setTimeout(() => setResetEmployeeSearch(false), 100);
      return;
    }

    if (employeeId === team.TeamLeaderId) {
      setSnackbarMessage("The team leader is already part of the team");
      setSnackbarSeverity("warning");
      setResetEmployeeSearch(true);
      setTimeout(() => setResetEmployeeSearch(false), 100);
      return;
    }

    setIsAddingMember(true);
    MaintenanceTeamsApi.AddTeamMember(team.Id, employeeId)
      .then(response => {
        if (response.status) {
          setSnackbarMessage("Member added successfully");
          setSnackbarSeverity("success");
          setResetEmployeeSearch(true);
          setTimeout(() => setResetEmployeeSearch(false), 100);
          if (onTeamUpdated) {
            onTeamUpdated();
          }
        } else {
          setSnackbarMessage(response.message || "Failed to add member");
          setSnackbarSeverity("error");
        }
      })
      .catch(() => {
        setSnackbarMessage("An error occurred while adding the member");
        setSnackbarSeverity("error");
      })
      .finally(() => {
        setIsAddingMember(false);
      });
  };

  if (!team) {
    return (
      <div className="text-muted text-[0.9rem]">
        Select a team to edit
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-4">
        <div className="flex flex-row items-center gap-2">
          <TextField
            label="Team Name"
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            size="small"
            className="w-[70%]"
            disabled={isUpdatingTeam}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleSaveTeamName}
            disabled={isUpdatingTeam || !teamName.trim()}
          >
            Save
          </Button>
        </div>

        <div>
          <div className="text-sm font-medium mb-1">
            Team Lead
          </div>
          <div className="flex flex-row items-center gap-2 mb-2">
            <ProfileImg
              imgKey={0}
              userName={employees.find(emp => emp.Id === team.TeamLeaderId)?.userName?.split('@')[0]}
            />
            <Chip
              label={employees.find(emp => emp.Id === team.TeamLeaderId)?.employee_name || "Unknown"}
              color="primary"
              variant="outlined"
            />
          </div>
          <div>
            <EmployeeSearch
              place_holder="Search and select new team leader"
              onSelectedEmployee={handleChangeTeamLeader}
              reset={resetTeamLeaderSearch}
              clearOnSelect={true}
              disabled={isUpdatingTeam}
              inputWidth={100}
            />
          </div>
        </div>

        <Divider className="mt-1 mb-1" />

        <div>
          <div className="text-[0.85rem] font-medium mb-1">
            Members
          </div>

          <div className="flex flex-col gap-1">
            {team?.TeamMembers?.map((member, idx) => {
              const memberEmployee = employees.find(emp => emp.Id === member.TeamMemberId);
              return (
                <div
                  key={member.TeamMemberId}
                  className="flex flex-row items-center gap-2"
                >
                  <ProfileImg
                    imgKey={idx}
                    userName={memberEmployee?.userName?.split('@')[0]}
                  />
                  <span className="text-[0.85rem] flex-grow-0">
                    {memberEmployee?.employee_name || "Unknown"}
                  </span>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveMemberClick(member.TeamMemberId, memberEmployee?.employee_name || "Unknown")}
                    disabled={isRemovingMember}
                    className="ml-auto"
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </div>
              );
            }) || (
              <span className="text-muted text-xs">
                No team members
              </span>
            )}
          </div>

          <div className="mt-3">
            <EmployeeSearch
              place_holder="Search and select an employee to add"
              onSelectedEmployee={handleAddMember}
              reset={resetEmployeeSearch}
              clearOnSelect={true}
              disabled={isAddingMember}
              inputWidth={100}
            />
          </div>
        </div>

        <Divider className="mt-1 mb-1" />

        <div className="flex flex-row gap-3">
          <Button
            color="error"
            size="small"
            variant="outlined"
            onClick={handleDeleteTeam}
            disabled={isDeletingTeam}
          >
            {isDeletingTeam ? "Deleting..." : "Delete Team"}
          </Button>
        </div>
      </div>

      <Dialog
        visible={deleteDialogOpen}
        max_width="sm"
        title="Delete Team"
        context={
          <div className="text-base">
            Are you sure you want to delete the team "{team?.TeamName}"? This action cannot be undone.
          </div>
        }
        onClose={confirmDeleteTeam}
        strict={true}
        show_options={true}
      />

      <Dialog
        visible={removeMemberDialogOpen}
        max_width="sm"
        title="Remove Team Member"
        context={
          <div className="text-base">
            Are you sure you want to remove {memberToRemove?.name} from the team?
          </div>
        }
        onClose={confirmRemoveMember}
        strict={true}
        show_options={true}
      />

      <Snackbar
        onSnackBarClose={() => setSnackbarMessage("")}
        open={snackbarMessage.length > 0}
        message={snackbarMessage}
        autoHide={true}
        horizontalPosition="right"
        severity={snackbarSeverity}
      />
    </div>
  );
};

export default EditTeam;
